import * as React from 'react';
import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  isRelativePathname,
  relativePathnameToAbsolute,
  useAbsoluteNavigation
} from 'utils/hooks/navigation/useNavigation';
import { Pathname } from 'utils/hooks/navigation/types';
import { LocationDescriptorObject } from 'history';
import { queryStringStringify } from 'src/utils/queryString';

export interface AppLinkWithWrapperBaseProps {
  url?: Pathname;
  // for edge case where this component is used by mui
  to?: Pathname | LocationDescriptorObject;
  // for edge case where this component is used by mui
  link?: Pathname;
  hash?: string;
  className?: string;
  basePath?: string;
  params?: any;
  paramsString?: string;
  stopPropagation?: boolean;
  preventDefault?: boolean;
  disabled?: boolean;
  hideTextDecoration?: boolean;
  style?: React.CSSProperties;
  dataTest?: string;
  state?: { [prop: string]: any };
  target?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  children?: any;
  onMouseEnter?: () => void;
}

export interface AppLinkWithWrapperProps extends AppLinkWithWrapperBaseProps {
  Component: React.FC<React.PropsWithChildren<any>>;
  componentProps?: object;
}

export const AppLinkWithWrapper = forwardRef<HTMLAnchorElement, AppLinkWithWrapperProps | AppLinkWithWrapperBaseProps>(
  (
    {
      hideTextDecoration,
      url,
      to,
      link,
      params,
      paramsString,
      onMouseEnter,
      state,
      children,
      hash,
      className,
      stopPropagation,
      preventDefault,
      onClick,
      dataTest,
      basePath,
      disabled,
      ...props
    },
    ref
  ) => {
    const { Component, componentProps, ...rest } = props as Partial<AppLinkWithWrapperProps>;
    const { params: baseParams, routeUrl: currentUrl } = useAbsoluteNavigation();
    const hasUrl = url || to || link;
    if (!hasUrl && !onClick) {
      return <>{children}</>;
    }
    let noTenantCodeUrl: string;
    // to be removed. This is overridden either way
    if (typeof to === 'object') {
      noTenantCodeUrl = (url ?? to.pathname ?? link ?? '').replace(`/${baseParams.tenantCode}`, '');
    } else {
      noTenantCodeUrl = (url ?? to ?? link ?? '').replace(`/${baseParams.tenantCode}`, '');
    }
    const [urlWithoutSearch, searchString] = noTenantCodeUrl.split('?');
    noTenantCodeUrl = urlWithoutSearch ?? '/';

    const targetTo: { pathname: string; hash?: string; search?: string; state?: { [prop: string]: any } } = {
      pathname: isRelativePathname(noTenantCodeUrl)
        ? relativePathnameToAbsolute(noTenantCodeUrl, currentUrl)
        : `${basePath === undefined ? `/${baseParams.tenantCode}` : basePath}${noTenantCodeUrl}`,
      state,
      hash
    };

    const onClickHandler = (e: React.MouseEvent<HTMLElement>) => {
      if (stopPropagation) {
        e.stopPropagation();
      }
      if (preventDefault) {
        e.preventDefault();
      }
      if (onClick) onClick(e);
    };

    if (searchString) targetTo.search = `?${searchString}`;
    if (paramsString) targetTo.search = `?${paramsString.replace('?', '')}`;
    else if (params) targetTo.search = `?${queryStringStringify(params, { arrayFormat: 'bracket' })}`;

    const AnchorComponent = hasUrl ? StyledLink : StyledA;
    const Wrapper = Component ? StyledCustomLink : AnchorComponent;

    return (
      <Wrapper
        $hideTextDecoration={hideTextDecoration}
        $disabled={disabled}
        onClick={onClickHandler}
        data-test={dataTest}
        className={className}
        tabIndex={Component ? -1 : undefined}
        to={targetTo}
        onMouseEnter={onMouseEnter}
        {...rest}
        ref={ref}
        role="link"
      >
        {Component ? (
          // @ts-ignore ignore typing issue here. All good
          <Component {...componentProps} className={`${componentProps?.className ?? ''} ${className ?? ''}`}>
            {children}
          </Component>
        ) : (
          children
        )}
      </Wrapper>
    );
  }
);

export const linkStyle = css<{ $hideTextDecoration?: boolean; $disabled?: boolean }>`
  && {
    pointer-events: all;
    color: ${({ theme }) => theme.colorPrimary};
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    gap: ${({ theme }) => theme.spacingSize.m};
    ${({ $disabled }) => $disabled && 'pointer-events: none;'}
    &:hover {
      color: ${({ theme }) => theme.colorPrimary};
      text-decoration: ${({ $hideTextDecoration }) => ($hideTextDecoration ? 'none' : 'underline')};
    }
    &:focus {
      box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colorPrimary};
    }
  }
`;

const StyledA = styled.a<{ $hideTextDecoration?: boolean; $disabled?: boolean }>`
  ${linkStyle}
`;

const StyledLink = styled(Link)<{ $hideTextDecoration?: boolean; $disabled?: boolean }>`
  ${linkStyle}
`;

const StyledCustomLink = styled(Link)<{ $hideTextDecoration?: boolean; $disabled?: boolean }>`
  && {
    text-decoration: none;
    ${({ $disabled }) => $disabled && 'pointer-events: none;'}
  }
`;
